// Risk profile display helpers.
// Canonical bands (8 questions × max 4 = score range 8–32),
// matching backend in profile.py:
//   8–15  → Conservative (blue)
//   16–23 → Moderate     (amber)
//   24–32 → Aggressive   (coral)
// Display-layer only — do not use for any calculation or gating logic.

export const RISK_MAX_SCORE = 32;
export const RISK_MIN_SCORE = 8;

const BAND_RANGES = {
  Conservative: [8, 15],
  Moderate: [16, 23],
  Aggressive: [24, 32],
};

export function getRiskBand(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return 'Moderate';
  if (s <= 15) return 'Conservative';
  if (s <= 23) return 'Moderate';
  return 'Aggressive';
}

export function getRiskBandColor(band) {
  switch (band) {
    case 'Conservative':
      return '#378ADD';
    case 'Aggressive':
      return '#D85A30';
    case 'Moderate':
    default:
      return '#BA7517';
  }
}

export function getRiskBandDescription(band) {
  switch (band) {
    case 'Conservative':
      return 'You prefer stability and capital preservation over high returns.';
    case 'Aggressive':
      return 'You\u2019re willing to accept significant volatility for higher long-term returns.';
    case 'Moderate':
    default:
      return 'You\u2019re comfortable with moderate fluctuations in pursuit of balanced growth.';
  }
}

// Percent within the global 8–32 scale (used for the progress bar fill on the
// onboarding result screen — unrelated to the band-internal percentile).
export function getRiskPercent(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return 0;
  const range = RISK_MAX_SCORE - RISK_MIN_SCORE;
  const pct = ((s - RISK_MIN_SCORE) / range) * 100;
  return Math.max(0, Math.min(100, pct));
}

/**
 * Position of a score WITHIN its band, expressed as an English label.
 *   percentile = (score - bandLow) / (bandHigh - bandLow)
 *   >= 0.67  → "Upper third"
 *   >= 0.34  → "Mid range"
 *    < 0.34  → "Lower third"
 * Returns null if the score is invalid (so callers can hide the line).
 */
export function getRiskBandPosition(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return null;
  const band = getRiskBand(s);
  const [low, high] = BAND_RANGES[band] || [RISK_MIN_SCORE, RISK_MAX_SCORE];
  const span = high - low;
  if (span <= 0) return 'Mid range';
  const ratio = (s - low) / span;
  if (ratio >= 0.67) return 'Upper third';
  if (ratio >= 0.34) return 'Mid range';
  return 'Lower third';
}

/**
 * Tooltip text — the only place where the raw "X/32" appears.
 */
export function getRiskTooltip(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return '';
  return `Your score: ${Math.round(s)}/${RISK_MAX_SCORE}`;
}
